import { Box, Divider, HStack, Icon, Image, StackProps, Text, VStack } from '@chakra-ui/react';
import { useCheckoutConfigContext } from '@contexts/CheckoutConfig';
import { useStepper } from '@contexts/Stepper';
import { DONATION_FREQUENCY, FormValues, LEGAL_PERSON, STEP } from '@types';
import { tDonationFrequency } from '@utils/translateEnum';
import { FC, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BsFillPersonFill } from 'react-icons/bs';
import { FaHandHoldingHeart } from 'react-icons/fa';
import { MdPayment } from 'react-icons/md';
import { Else, If, Then, When } from 'react-if';

const StepperMobile: FC<StackProps> = (props) => {
  const { checkout } = useCheckoutConfigContext();
  const { watch } = useFormContext<FormValues>();
  const { currentStep, lastStep, goToStep } = useStepper();
  const [logoSrc, setLogoSrc] = useState<string | null>(checkout.organization.logo);
  const [coverSrc, setCoverSrc] = useState<string | null>(checkout.campaign.cover_url);
  const [peerLogoSrc, setPeerLogoSrc] = useState<string | null>(
    checkout.peer_campaign?.supporter_image_src || null,
  );
  const { t } = useTranslation();

  const paymentMethodId = watch('paymentMethodId');

  const selectedMethodLabel = useMemo(() => {
    if (paymentMethodId && checkout) {
      const method = checkout.enabled_payment_methods.find(
        (method) => method.id === paymentMethodId,
      );
      return method?.label;
    }

    return '';
  }, [paymentMethodId, checkout]);

  return (
    <VStack w="full">
      <When condition={!!checkout.peer_campaign}>
        <HStack w="full" justify="start" mb={4} spacing={6}>
          <VStack w="full" align="start" spacing={0}>
            <Text fontWeight="semibold" color="blackAlpha.800">
              {checkout.peer_campaign?.supporter_name}
            </Text>

            <When condition={checkout.peer_campaign?.supporter_message}>
              <Text fontSize="sm" color="blackAlpha.600">
                <If
                  condition={
                    checkout.peer_campaign?.supporter_message &&
                    checkout.peer_campaign?.supporter_message.length > 100
                  }
                >
                  <Then>
                    {checkout.peer_campaign?.supporter_message &&
                      checkout.peer_campaign?.supporter_message.slice(0, 100) + '...'}
                  </Then>
                  <Else>{checkout.peer_campaign?.supporter_message}</Else>
                </If>
              </Text>
            </When>
          </VStack>

          <When condition={peerLogoSrc}>
            <Image
              src={peerLogoSrc!}
              maxH="100px"
              maxW="100px"
              borderRadius="full"
              onError={() => setPeerLogoSrc(null)}
              alt={checkout.organization.name || checkout.campaign.title || 'logo'}
            />
          </When>
        </HStack>
      </When>

      <HStack w="full" justify="start" mb={4} spacing={4}>
        <When condition={logoSrc}>
          <Image
            src={logoSrc!}
            borderRadius="lg"
            maxH="80px"
            maxW="80px"
            onError={() => setLogoSrc(null)}
            alt={checkout.organization.name || checkout.campaign.title || 'logo'}
          />
        </When>

        <VStack w="full" align="start" spacing={0}>
          <Text fontWeight="semibold" color="blackAlpha.600">
            {checkout.organization.name}
          </Text>

          <Text fontSize="xl" fontWeight="semibold" color="blackAlpha.800">
            {checkout.campaign.title}
          </Text>
        </VStack>
      </HStack>

      <When condition={!!checkout.cost_example}>
        <HStack w="full" spacing={4} alignItems="flex-start" pb={4}>
          <Box
            backgroundImage={`url(${checkout.cost_example?.image})`}
            backgroundSize="cover"
            backgroundPosition="center"
            backgroundRepeat="no-repeat"
            w="100px"
            h="100px"
            borderRadius="lg"
          />
          <Box>
            <Text fontWeight="bold">{checkout.cost_example?.name}</Text>
            <div dangerouslySetInnerHTML={{ __html: checkout.cost_example?.description || '' }} />
          </Box>
        </HStack>
      </When>

      {/* <When condition={coverSrc}>
        <Box pb={4} w="full">
          <Box position="relative">
            <Center>
              <Image
                src={coverSrc!}
                borderRadius="lg"
                maxW={{ base: 'full', sm: '400px', md: '500px' }}
                onError={() => setCoverSrc(null)}
                alt={checkout.campaign.title || 'cover'}
              />
            </Center>

            <When
              condition={checkout?.campaign.description && checkout.campaign.description.length}
            >
              <Box position="absolute" bottom={0} left={0} right={0} h="auto">
                <Center>
                  <Box
                    p={2}
                    maxW={{ base: 'full', sm: '400px', md: '500px' }}
                    backgroundColor="whiteAlpha.800"
                  >
                    <Text fontSize="sm">
                      <If
                        condition={
                          checkout?.campaign.description &&
                          checkout.campaign.description.length > 100
                        }
                      >
                        <Then>
                          {checkout?.campaign.description &&
                            checkout.campaign.description.slice(0, 100) + '...'}
                        </Then>
                        <Else>{checkout.campaign.description}</Else>
                      </If>
                    </Text>
                  </Box>
                </Center>
              </Box>
            </When>
          </Box>
        </Box>
      </When> */}

      {/* DONATION STEP */}
      <VStack w="full" {...props}>
        <Box w="full">
          <HStack spacing={2} w="full" justify="space-between">
            <HStack
              color={currentStep == STEP.DONATION ? 'primary.400' : 'blackAlpha.600'}
              spacing={1}
              transition="all ease-in-out 0.2s"
              onClick={() => goToStep(STEP.DONATION)}
            >
              <Icon as={FaHandHoldingHeart} mr={2} />
              <Text fontWeight="medium">{t('How much')}</Text>
            </HStack>

            <Text fontWeight="medium">
              {watch('amount')}€{' '}
              <When condition={watch('frequency') != DONATION_FREQUENCY.ONEOFF}>
                {tDonationFrequency(watch('frequency'))}
              </When>
            </Text>
          </HStack>
        </Box>

        <Divider />

        {/* SUPPORTER STEP */}
        <Box w="full">
          <HStack spacing={2} w="full" justify="space-between">
            <HStack
              color={currentStep == STEP.SUPPORTER ? 'primary.400' : 'blackAlpha.600'}
              spacing={1}
              transition="all ease-in-out 0.2s"
              onClick={() => goToStep(STEP.SUPPORTER)}
            >
              <Icon as={BsFillPersonFill} mr={2} />
              <Text fontWeight="medium">{t('From')}</Text>
            </HStack>

            <If condition={watch('legalPerson') === LEGAL_PERSON.INDIVIDUAL}>
              <Then>
                <Text fontSize="md" fontWeight="medium">
                  {watch('firstName')} {watch('lastName')}
                </Text>
              </Then>
              <Else>
                <Text fontSize="md" fontWeight="medium">
                  {watch('businessName')}
                </Text>
              </Else>
            </If>
          </HStack>
        </Box>

        <Divider />

        {/* PAYMENT STEP */}
        <Box w="full">
          <HStack spacing={2} w="full" justify="space-between">
            <HStack
              color={currentStep == STEP.PAYMENT ? 'primary.400' : 'blackAlpha.600'}
              spacing={1}
              transition="all ease-in-out 0.2s"
              {...((lastStep === STEP.SUPPORTER || lastStep === STEP.PAYMENT) && {
                onClick: () => goToStep(STEP.PAYMENT),
              })}
            >
              <Icon as={MdPayment} mr={2} />
              <Text fontWeight="medium">{t('Payment method')}</Text>
            </HStack>

            <When condition={lastStep === STEP.PAYMENT}>
              <Text fontWeight="medium">{selectedMethodLabel}</Text>
            </When>
          </HStack>
        </Box>
      </VStack>
    </VStack>
  );
};

export default StepperMobile;
